import AsyncStorage from '@react-native-async-storage/async-storage';
import { createContext, useContext, useSyncExternalStore } from 'react';

import type { NotificationVariant } from '@/utils/notification-manager';

/**
 * A history of the toasts the app has shown, so one that vanished before anyone read it can be
 * found again (#633).
 *
 * Generic on purpose: any screen records through `useNotify` (hooks/use-notify.ts), and the
 * header bell reads it back. Client-side only — there is no server activity feed this fits.
 *
 * ── PERSISTED PER USER ──
 *
 * Kept in AsyncStorage (localStorage on web) so it survives a reload, under a key carrying the
 * OWNER's user id. Sign-out does not clear storage, so an unscoped key would show the next
 * person on a shared browser everything the previous one did. `setNotificationHistoryOwner`
 * runs on every identity change: it drops what is in memory, deletes every history key that is
 * not the new owner's, and loads the owner's own. With no owner nothing is persisted at all.
 */

export interface NotificationHistoryEntry {
	id: string;
	message: string;
	variant: NotificationVariant;
	/** Real moment (ms) — render viewer-local. */
	createdAt: number;
	/** Where the thing the toast was about lives, e.g. `/accounting/account/<id>`. */
	href?: string;
	/** Which feature raised it ("Ownership transfers"), for the history list. */
	source?: string;
	read: boolean;
}

export const NOTIFICATION_HISTORY_PREFIX = 'notification-history:';
/** Plenty to scroll back through a day's work without growing storage without bound. */
export const NOTIFICATION_HISTORY_LIMIT = 100;

export const notificationHistoryKey = (userId: string): string => `${NOTIFICATION_HISTORY_PREFIX}${userId}`;

let owner: string | null = null;
let entries: NotificationHistoryEntry[] = [];
/**
 * The owner's stored history has been read and merged into `entries`. Until then nothing is
 * written: a record made while the load is in flight would otherwise persist ONLY itself over
 * the stored history, and the older entries — merged into memory a moment later but never
 * written back — would be gone on the next reload. The load writes the merged result itself.
 */
let loaded = false;
const listeners = new Set<() => void>();

const emit = () => {
	for (const listener of listeners) listener();
};

const persist = () => {
	if (!owner || !loaded) return;
	// Best-effort: the history is a convenience and must never break the action that raised it.
	void AsyncStorage.setItem(notificationHistoryKey(owner), JSON.stringify(entries)).catch(() => undefined);
};

const isEntry = (value: unknown): value is NotificationHistoryEntry => {
	const e = value as NotificationHistoryEntry | null;
	return !!e && typeof e.id === 'string' && typeof e.message === 'string' && typeof e.createdAt === 'number';
};

/**
 * Make `userId` the only person whose history exists. Null (signed out) clears everything.
 * Resolves once the owner's stored history is loaded.
 */
export async function setNotificationHistoryOwner(userId: string | null): Promise<void> {
	owner = userId;
	entries = [];
	loaded = false;
	emit();
	let stored: NotificationHistoryEntry[] = [];
	try {
		const keys = await AsyncStorage.getAllKeys();
		const foreign = keys.filter((k) => k.startsWith(NOTIFICATION_HISTORY_PREFIX)
			&& (!userId || k !== notificationHistoryKey(userId)));
		if (foreign.length) await AsyncStorage.multiRemove(foreign);
		if (!userId) return;
		const raw = await AsyncStorage.getItem(notificationHistoryKey(userId));
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		stored = Array.isArray(parsed) ? parsed.filter(isEntry) : [];
	} catch {
		// A corrupt record or unavailable storage leaves the stored side empty, never a broken app.
	}
	// The owner may have changed while storage was being read; never load into someone else's.
	if (!userId || owner !== userId) return;
	// ONE merge-and-write: anything recorded while the load was in flight is newer, so it stays on
	// top, and the merged whole is written back so the stored entries survive the next reload.
	const seen = new Set(entries.map((e) => e.id));
	entries = [...entries, ...stored.filter((e) => !seen.has(e.id))].slice(0, NOTIFICATION_HISTORY_LIMIT);
	loaded = true;
	emit();
	// Only when something was recorded during the load — otherwise storage already holds this.
	if (seen.size > 0) persist();
}

/** Add one entry to the top of the history. */
export function recordNotification(
	entry: Pick<NotificationHistoryEntry, 'message' | 'variant' | 'href' | 'source'>,
): NotificationHistoryEntry {
	const created: NotificationHistoryEntry = {
		...entry,
		id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
		createdAt: Date.now(),
		read: false,
	};
	entries = [created, ...entries].slice(0, NOTIFICATION_HISTORY_LIMIT);
	emit();
	persist();
	return created;
}

export function markNotificationHistoryRead(): void {
	if (!entries.some((e) => !e.read)) return;
	entries = entries.map((e) => (e.read ? e : { ...e, read: true }));
	emit();
	persist();
}

export function clearNotificationHistory(): void {
	entries = [];
	emit();
	persist();
}

export const getNotificationHistory = (): NotificationHistoryEntry[] => entries;

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const EMPTY: NotificationHistoryEntry[] = [];

/**
 * Who is LOOKING at the history — the signed-in user, set by `NotificationProvider`.
 *
 * Reads are guarded by it so a missed clear can never leak: on sign-out the authenticated shell
 * UNMOUNTS rather than re-rendering with no user, so the store's owner can still be the previous
 * user when the next one's shell first renders — before its effect rebinds the owner. Undefined
 * (no provider declared one: specs, print routes) reads unguarded.
 */
const ViewerContext = createContext<string | null | undefined>(undefined);
export const NotificationHistoryViewer = ViewerContext.Provider;

/** The history, newest first, re-rendering on every change — empty unless it belongs to the viewer. */
export const useNotificationHistory = (): NotificationHistoryEntry[] => {
	const viewer = useContext(ViewerContext);
	const all = useSyncExternalStore(subscribe, getNotificationHistory, getNotificationHistory);
	if (viewer === undefined) return all;
	return viewer !== null && viewer === owner ? all : EMPTY;
};
